import LazyVideo from "@/components/LazyVideo";
import React, { useState, useEffect, useRef } from 'react';
import { Seo } from '../components/Seo';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { productsData, shopCategories } from '../data/content';
import { Leaf, Plus, ShoppingBag, Check, Droplet } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useContent } from '@/context/ContentContext';
import type { BundleView } from '@/types/bundle';

const priceNum = (p: string) => parseInt(p.replace(/\D/g, ''), 10) || 0;

const ProductCard: React.FC<{ product: typeof productsData[number]; index: number; compact?: boolean }> = ({ product, index, compact = false }) => {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    addToCart({ id: product.id, name: product.name, price: priceNum(product.price), quantity: 1, image: product.image });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-5% 0px' }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      className="group relative bg-white border border-[#412018]/5 overflow-hidden flex flex-col h-full shadow-sm hover:shadow-2xl hover:shadow-[#67392b]/10 transition-all duration-500"
    >
      <Link to={`/shop/${product.id}`} aria-label={product.name} className="absolute inset-0 z-0" />

      <div className="aspect-square w-full overflow-hidden relative shrink-0">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-[2000ms] ease-luxury md:group-hover:scale-110 grayscale-[10%] md:group-hover:grayscale-0"
        />
        <div className="absolute top-4 right-4 z-20 bg-white/90 backdrop-blur px-3 py-1 rounded-sm flex items-center gap-2 shadow-sm">
          <Leaf size={12} className="text-green-700" />
          <span className="text-[10px] uppercase tracking-wider text-[#412018]">100% טבעי</span>
        </div>
      </div>

      <div className={`${compact ? 'p-4 md:p-5' : 'p-6 md:p-8'} pb-2 flex flex-col flex-1 relative z-10 pointer-events-none`}>
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#67392b] font-bold mb-3">{product.sub}</p>
        <div className="flex justify-between items-baseline mb-4 gap-3">
          <h3 className={`font-serif leading-[0.95] tracking-wide text-[#412018] group-hover:text-[#67392b] transition-colors ${compact ? 'text-xl md:text-2xl' : 'text-3xl md:text-4xl lg:text-[2.6rem]'}`}>
            {product.name}
          </h3>
          <span className="flex items-baseline gap-2 whitespace-nowrap pt-1">
            <span className="font-sans font-light tabular-nums text-[#412018] text-lg leading-none">{product.price}</span>
            {(product as any).compareAtPrice && (
              <span className="font-sans font-light tabular-nums text-[#412018]/40 text-sm leading-none line-through">
                {(product as any).compareAtPrice}
              </span>
            )}
          </span>
        </div>
        <div className="w-10 h-px bg-[#412018]/15 mb-5 group-hover:bg-[#67392b]/40 transition-colors duration-500" />
        <p className={`text-[#412018]/70 font-light leading-[1.7] mb-6 flex-grow ${compact ? 'text-[12px] line-clamp-2' : 'text-[14px] line-clamp-3'}`}>
          {product.description}
        </p>
      </div>

      <div className={`mt-auto flex w-full border-t border-[#412018]/10 relative z-10 pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto`}>
        <Link
          to={`/shop/${product.id}`}
          className={`flex-[1] bg-[#F9F7F2] hover:bg-[#EAE6D7] text-[#412018] ${compact ? 'py-3 px-2' : 'py-5 px-4'} flex items-center justify-center border-l border-[#412018]/10 transition-colors duration-300 group/sec-btn`}
        >
          <div className="flex items-center gap-2">
            <Plus size={12} className="text-[#412018]/40 group-hover/sec-btn:rotate-90 transition-transform duration-300" />
            <span className="text-[10px] font-bold tracking-[0.15em] uppercase">פרטים</span>
          </div>
        </Link>
        <button
          onClick={handleAdd}
          className={`flex-[2.5] ${compact ? 'py-3 px-3 gap-2' : 'py-5 px-6 gap-3'} flex items-center justify-center transition-all duration-500 group/main-btn relative overflow-hidden ${
            added ? 'bg-green-600 text-white' : 'bg-[#412018] hover:bg-[#67392b] text-[#fbbf25]'
          }`}
        >
          <span className={`relative z-10 font-bold tracking-[0.2em] uppercase whitespace-nowrap ${compact ? 'text-[9px]' : 'text-[11px]'}`}>
            {added ? 'נוסף לסל' : 'הוספה לסל'}
          </span>
          {added ? (
            <Check size={compact ? 12 : 16} className="relative z-10" />
          ) : (
            <ShoppingBag size={compact ? 12 : 16} className="relative z-10 text-[#fbbf25] group-hover/main-btn:-translate-y-1 transition-transform duration-300" />
          )}
          <div className="absolute inset-0 bg-white/5 translate-x-full group-hover/main-btn:translate-x-[-150%] transition-transform duration-700 skew-x-12 pointer-events-none" />
        </button>
      </div>
    </motion.div>
  );
};

const BundleCard: React.FC<{ bundle: BundleView; index: number }> = ({ bundle, index }) => {
  const { addBundle } = useCart();
  const { products } = useContent();
  const [added, setAdded] = useState(false);
  const items = bundle.productIds
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean) as typeof productsData;

  const original = items.reduce((s, p) => s + priceNum(p.price), 0);
  const total =
    bundle.priceOverride != null
      ? Math.round(bundle.priceOverride)
      : Math.round(original * (1 - bundle.discount));
  const compareAt =
    bundle.compareAtOverride != null ? Math.round(bundle.compareAtOverride) : original;
  const discountPct =
    bundle.priceOverride != null && compareAt > 0
      ? Math.round((1 - total / compareAt) * 100)
      : Math.round(bundle.discount * 100);

  const handleAddBundle = () => {
    addBundle({
      slug: bundle.slug,
      title: bundle.name,
      image: bundle.image,
      price: total,
      compareAt,
      discountCode: bundle.discountCode,
      components: items.map((p) => ({
        id: p.id,
        name: p.name,
        price: priceNum(p.price),
        image: p.image,
        quantity: 1,
      })),
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };


  const reverseRow = index % 2 === 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-5% 0px' }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-stretch border-b border-[#412018]/15 pb-16 md:pb-24 last:border-0 last:pb-0 ${
        reverseRow ? 'lg:[direction:ltr]' : ''
      }`}
    >
      {/* Composition / images — order on mobile: middle */}
      <div className="lg:col-span-7 [direction:rtl] order-2 lg:order-none">
        <div className="relative bg-[#412018] aspect-[4/3] md:aspect-[16/10] overflow-hidden">
          {bundle.video ? (
            <video
              src={bundle.video}
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : bundle.image ? (
            <img
              src={bundle.image}
              alt={bundle.name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
            />
          ) : (
            <div className={`absolute inset-0 grid gap-px bg-[#412018]/40 ${items.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {items.map((p) => (
                <div key={p.id} className="relative bg-white overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105" />
                </div>
              ))}
            </div>
          )}
          {discountPct > 0 && (
            <div className="absolute top-5 right-5 bg-[#fbbf25] text-[#412018] px-3 py-1 text-[10px] font-bold tracking-[0.25em] uppercase">
              חיסכון {discountPct}%
            </div>
          )}
        </div>
      </div>

      {/* Details — split for mobile reorder */}
      <div className="lg:col-span-5 [direction:rtl] flex flex-col lg:justify-center order-1 lg:order-none contents lg:flex">
        {/* TOP block (mobile: above image) */}
        <div className="order-1 lg:order-none">
          <span className="text-[10px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-4 block">
            {bundle.sub}
          </span>
          <h3 className="font-serif text-[#412018] leading-[0.95] tracking-wide text-4xl md:text-5xl lg:text-[3.4rem] mb-4">
            {bundle.name}
          </h3>
          <p className="text-[#67392b] italic text-base md:text-lg mb-5">{bundle.tagline}</p>
          <p className="text-[#412018]/75 font-light leading-relaxed text-[15px] md:text-base mb-7 max-w-xl">
            {bundle.description}
          </p>

          <ul className="space-y-2.5 mb-2 lg:mb-8">
            {items.map((p) => (
              <li key={p.id} className="flex items-center gap-3 text-[#412018]/85 text-sm">
                <span className="w-6 h-6 rounded-full bg-[#fbbf25]/20 border border-[#fbbf25]/50 flex items-center justify-center shrink-0">
                  <Droplet size={11} className="text-[#67392b] fill-[#67392b]/30" strokeWidth={2} />
                </span>
                <Link to={`/shop/${p.id}`} className="hover:text-[#67392b] transition-colors">
                  {p.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* BOTTOM block (mobile: below image — price + CTA in one row) */}
        <div className="order-3 lg:order-none mt-6 lg:mt-0 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-baseline gap-3">
            <span className="font-sans font-light text-3xl md:text-4xl text-[#412018] tabular-nums leading-none">
              ₪{total}
            </span>
            {compareAt > total && (
              <span className="text-[#412018]/40 line-through text-base">₪{compareAt}</span>
            )}
          </div>
          <button
            onClick={handleAddBundle}
            className={`group/cta relative overflow-hidden px-7 py-3.5 transition-colors duration-500 ${
              added ? 'bg-green-600 text-white' : 'bg-[#412018] text-[#fbbf25]'
            }`}
          >
            <span className="absolute inset-0 bg-[#fbbf25] translate-y-full group-hover/cta:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.7,0,0.2,1)]" />
            <span className={`relative flex items-center gap-2.5 transition-colors duration-500 ${added ? '' : 'group-hover/cta:text-[#412018]'}`}>
              {added ? <Check size={14} /> : <ShoppingBag size={14} />}
              <span className="text-[11px] font-bold uppercase tracking-[0.28em]">
                {added ? 'נוסף לסל' : 'הוספה לסל'}
              </span>
            </span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const CategoryHeader: React.FC<{ cat: typeof shopCategories[number] }> = ({ cat }) => {
  const [first, ...rest] = cat.label.split(' ');
  return (
    <div className="max-w-xl text-right ml-auto">
      <h2 className="font-serif leading-[0.95] tracking-wide text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#EAE6D7] mb-5">
        {first} <span className="text-[#fbbf25] italic">{rest.join(' ')}</span>
      </h2>
      <div className="w-12 h-px bg-[#fbbf25] mb-5 ml-auto" />
      <p className="text-[#EAE6D7]/85 text-sm md:text-base font-light leading-relaxed">
        {cat.desc}
      </p>
    </div>
  );
};

const VideoPanel: React.FC<{ cat: typeof shopCategories[number]; align?: 'right' | 'left'; className?: string }> = ({ cat, align = 'right', className = '' }) => (
  <div className={`relative w-full h-full overflow-hidden bg-[#412018] ${className}`}>
    {cat.mediaType === 'video' ? (
      <LazyVideo src={cat.media} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover"  aria-hidden="true" />
    ) : (
      <img src={cat.media} alt={cat.label} className="absolute inset-0 w-full h-full object-cover" />
    )}
    <div className="absolute inset-0 pointer-events-none bg-gradient-to-l from-[#412018]/95 via-[#412018]/55 to-[#412018]/15" />
    <div
      className="absolute inset-0 flex items-end justify-end p-6 sm:p-8 md:p-10 lg:p-14"
      dir="rtl"
    >
      <CategoryHeader cat={cat} />
    </div>
  </div>
);

const CategoryLayout: React.FC<{ cat: typeof shopCategories[number]; items: typeof productsData }> = ({ cat, items }) => {
  if (cat.id === 'hair') {
    return (
      <div className="space-y-6 lg:space-y-0">
        {/* Desktop: всё в один ряд */}
        <div className="hidden lg:flex gap-4 items-stretch">
          <div className="flex-[1.4] min-w-0 self-stretch">
            <div className="relative h-full min-h-[440px]">
              <VideoPanel cat={cat} align="right" />
            </div>
          </div>
          {items.map((p, i) => (
            <div key={p.id} className="flex-1 min-w-0 flex">
              <div className="w-full">
                <ProductCard product={p} index={i} compact />
              </div>
            </div>
          ))}
        </div>

        {/* Mobile/tablet: видео сверху, товары грид */}
        <div className="lg:hidden space-y-6">
          <div className="relative aspect-[4/5] sm:aspect-[16/10] -mx-6 sm:mx-0">
            <VideoPanel cat={cat} align="right" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {items.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (cat.id === 'face') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
        {/* 2 products on right (desktop) */}
        <div className="lg:col-span-8 order-2 lg:order-1 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {items.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
        {/* Video on left (desktop) — на мобильном идёт ПЕРЕД товарами и во всю ширину */}
        <div className="lg:col-span-4 order-1 lg:order-2">
          <div className="relative aspect-[4/5] lg:aspect-auto lg:h-full min-h-[500px] -mx-6 lg:mx-0">
            <VideoPanel cat={cat} />
          </div>
        </div>
      </div>
    );
  }

  // body: mirror of the "face" layout — video stays on the visual right, products (2) on the left
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
      {/* Video — на мобильном первым и во всю ширину */}
      <div className="lg:col-span-4 order-1">
        <div className="relative aspect-[4/5] lg:aspect-auto lg:h-full min-h-[500px] -mx-6 lg:mx-0">
          <VideoPanel cat={cat} />
        </div>
      </div>
      {/* 2 products */}
      <div className="lg:col-span-8 order-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
        {items.map((p, i) => (
          <ProductCard key={p.id} product={p} index={i} />
        ))}
      </div>
    </div>
  );
};


export const Shop: React.FC = () => {
  const { products: productsData, shopCategories, bundles } = useContent();
  const [activeCategory, setActiveCategory] = useState<string>('bundles');
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 3;
      let current = 'bundles';
      const order = ['bundles', ...shopCategories.map((c) => c.id)];
      for (const id of order) {
        const el = sectionRefs.current[id];
        if (el && el.offsetTop <= scrollPosition) current = id;
      }
      setActiveCategory(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = sectionRefs.current[id];
    if (el) {
      const offset = 140;
      const top = el.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const navItems = [
    { id: 'bundles', label: 'מארזים' },
    ...shopCategories.map((c) => ({ id: c.id, label: c.label })),
  ];

  return (
    <>
      <Seo
        title="חנות מוצרי טיפוח טבעיים | RIHAM Shop"
        description="חנות RIHAM — מוצרי טיפוח טבעיים לעור ולשיער, נרקחים בקליניקה של רהאם מנסור משילוב צמחי מרפא ושמנים."
        path="/shop"
      />
    <div className="bg-[#EAE6D7] min-h-[100dvh] pt-32 pb-24 text-[#412018] relative" dir="rtl">
      <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply" />

      {/* HERO */}
      <section className="relative w-full min-h-[640px] h-[88vh] max-h-[920px] overflow-hidden -mt-32 mb-12 md:mb-20">
        <LazyVideo
          src="https://cdn.shopify.com/videos/c/o/v/0971090ff8e54fdcaff90d1707f5caa0.mp4"
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
         aria-hidden="true" />
        <div className="absolute inset-0 bg-[hsl(47_31%_88%/0.55)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(47_31%_88%/0.3)] via-transparent to-[hsl(47_31%_88%/0.4)]" />
        <div
          className="absolute -bottom-px left-0 right-0 h-[60%] pointer-events-none"
          style={{
            background:
              'linear-gradient(to top, hsl(47 31% 88%) 0%, hsl(47 31% 88% / 0.9) 18%, hsl(47 31% 88% / 0.7) 38%, hsl(47 31% 88% / 0.35) 62%, hsl(47 31% 88% / 0) 100%)',
          }}
        />
        <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply" />

        <div className="relative h-full flex flex-col items-center justify-center text-center px-6 pt-28 pb-24 md:pb-32">
          <motion.span
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-6"
          >
            הבוטיק של רהאם מנסור
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-[#412018] leading-[0.9] tracking-wide text-[3rem] sm:text-7xl md:text-8xl lg:text-[8.5rem] xl:text-[10rem]"
          >
            רוקחות <span className="text-[#67392b] italic">טבעית</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-6 max-w-2xl text-[#412018]/75 text-base md:text-lg font-light leading-relaxed"
          >
            פורמולות עתיקות שנרקחות במעבדה שלנו — מוכנות במלאי לטיפוח העור והשיער שלך.
          </motion.p>

          <motion.nav
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-10 md:mt-14 w-full max-w-5xl flex flex-col md:flex-row items-stretch md:items-baseline justify-center gap-3 md:gap-10 lg:gap-14"
          >
            {navItems.map((g, i) => {
              const isActive = activeCategory === g.id;
              return (
                <React.Fragment key={g.id}>
                  {i > 0 && <span className="hidden md:block w-px h-7 bg-[#412018]/25 self-center" aria-hidden />}
                  <button
                    onClick={() => scrollToSection(g.id)}
                    className="group relative flex-1 md:flex-initial text-center transition-all duration-300 py-2"
                  >
                    <span className={`block font-serif tracking-wide transition-colors text-[2rem] sm:text-[2.4rem] md:text-2xl lg:text-3xl leading-[1.05] ${
                      isActive ? 'text-[#67392b] italic' : 'text-[#412018] group-hover:text-[#67392b]'
                    }`}>
                      {g.label}
                    </span>
                    <span className={`mx-auto mt-3 block h-px bg-[#67392b] transition-all duration-500 ${
                      isActive ? 'w-12 opacity-100' : 'w-0 opacity-0 group-hover:w-12 group-hover:opacity-100'
                    }`} />
                  </button>
                </React.Fragment>
              );
            })}
          </motion.nav>
        </div>
      </section>

      <div className="container mx-auto px-6 relative z-10 max-w-[1400px]">
        {/* BUNDLES */}
        <section
          ref={(el) => { sectionRefs.current['bundles'] = el; }}
          className="scroll-mt-44 mb-32 md:mb-44"
        >
          <div className="flex items-center gap-4 mb-10 md:mb-14">
            <span className="text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-[#412018]/60 font-bold">
              Curated Bundles
            </span>
            <span className="flex-1 h-px bg-[#412018]/15" />
            <span className="hidden md:inline text-[10px] uppercase tracking-[0.3em] text-[#412018]/50">
              {bundles.length} מארזים
            </span>
          </div>

          <div className="max-w-3xl mb-14 md:mb-20">
            <h2 className="font-serif leading-[0.95] tracking-wide text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] mb-7">
              <span className="text-[#412018]">המארזים</span>{' '}
              <span className="text-[#67392b] italic">שלנו</span>
            </h2>
            <div className="w-12 h-px bg-[#67392b] mb-6" />
            <p className="text-[#412018]/75 text-base md:text-lg font-light leading-relaxed">
              שלוש סדרות אוצרות שמשלבות בין מוצרי הבית לחוויית טיפוח שלמה — במחיר משתלם יותר משילוב בנפרד.
            </p>
          </div>

          <div className="space-y-16 md:space-y-24">
            {bundles.map((b, i) => (
              <BundleCard key={b.id} bundle={b} index={i} />
            ))}
          </div>
        </section>

        {/* CATEGORIES */}
        <div className="space-y-32 md:space-y-44">
          {shopCategories.map((cat) => {
            const items = productsData.filter((p) => p.category === cat.id);
            if (!items.length) return null;
            return (
              <section
                key={cat.id}
                ref={(el) => { sectionRefs.current[cat.id] = el; }}
                className="scroll-mt-44"
              >
                <div className="flex items-center gap-4 mb-10 md:mb-14">
                  <span className="text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-[#412018]/60 font-bold">
                    {cat.subtitle}
                  </span>
                  <span className="flex-1 h-px bg-[#412018]/15" />
                  <span className="hidden md:inline text-[10px] uppercase tracking-[0.3em] text-[#412018]/50">
                    {items.length} מוצרים
                  </span>
                </div>

                <CategoryLayout cat={cat} items={items} />
              </section>
            );
          })}
        </div>
      </div>
    </div>
    </>
  );
};
